/**
 * Client-side shapes for the meeting-perf API responses + small view helpers.
 * The response/row shapes derive from the server's route defs (Phase 7) via
 * `./api` — never redeclared by hand. Two kinds of exception stay hand-written:
 * UI-only constants/helpers, and the jsonb-narrowing types (`CategoryScore`,
 * `NotableQuote`, `Analysis`) — the analysis jsonb columns are loosely typed on
 * the server row, but the detail route's enrichment (`enrichAnalysis`)
 * guarantees the fields at runtime, so the client narrows them once here.
 */
import type { meetingPerfApi } from "./api";

export const CATEGORY_KEYS = [
	"clarity",
	"conciseness",
	"confidence",
	"engagement",
	"structure",
	"professionalism",
] as const;
export type CategoryKey = (typeof CATEGORY_KEYS)[number];

export const CATEGORY_LABELS: Record<CategoryKey, string> = {
	clarity: "Clarity",
	conciseness: "Conciseness",
	confidence: "Confidence",
	engagement: "Engagement",
	structure: "Structure",
	professionalism: "Professionalism",
};

// One chart-series color per category (overall uses --color-foreground).
export const CATEGORY_COLORS: Record<CategoryKey, string> = {
	clarity: "var(--color-chart-1)",
	conciseness: "var(--color-chart-2)",
	confidence: "var(--color-chart-3)",
	engagement: "var(--color-chart-4)",
	structure: "var(--color-chart-5)",
	professionalism: "var(--color-chart-6)",
};

/** Narrow an untyped-text `affiliation` column to the route's Zod enum — the
 *  app-level constraint on the column (D12-63); null = unset. */
type WithAffiliation<S> = Omit<S, "affiliation"> & {
	affiliation: "internal" | "external" | null;
};

// ── Meetings ─────────────────────────────────────────────────────────────────

/** The paginated page returned by `GET /meetings`. */
export type MeetingsPage = Awaited<ReturnType<typeof meetingPerfApi.listMeetings>>;

/** One meetings-list row — the meeting row plus the aggregate `score` and the
 *  viewer's derived `involvement`. */
export type Meeting = MeetingsPage["items"][number];

/** Display label for a meeting type value: trims whitespace and falls back to "Untagged". */
export function meetingTypeLabel(mt: string | null | undefined): string {
	return mt?.trim() || "Untagged";
}

// ── Speakers ─────────────────────────────────────────────────────────────────

/** One enriched speaker row (`GET /speakers`) — per-speaker aggregates joined in. */
export type Speaker = WithAffiliation<
	Awaited<ReturnType<typeof meetingPerfApi.listSpeakers>>[number]
>;

// ── Analyses (jsonb narrowings — see module docstring) ───────────────────────

export type CategoryScore = {
	score: number;
	weight: number;
	metrics: Record<string, unknown>;
	/** Deprecated alias for `analysis`. Present on old rows; omitted on new ones. */
	feedback?: string;
	/** Long-form per-dimension narrative for the Analyze pane. The detail route
	 *  falls back to `feedback` for old rows so this is always present. */
	analysis: string;
	wins: string[];
	changes: string[];
	/** Added by WS-A · A4.1. Indicates whether metrics came from Fireflies'
	 *  deterministic counts ("fireflies") or AI estimation ("ai"). Absent on
	 *  old rows — treat undefined as "ai". */
	metricsSource?: "fireflies" | "ai" | null;
};

export type NotableQuote = {
	quote: string;
	context: string;
	sentiment: "positive" | "neutral" | "negative" | string;
	/** Which dimension this quote evidences. Always present after route derivation. */
	dimensionKey: CategoryKey;
	/** Seconds from meeting start. Always present after route derivation. */
	timestampSec: number;
	/** Short clause used as the annotation chip body. */
	note: string;
};

type MeetingDetailWire = Awaited<ReturnType<typeof meetingPerfApi.meetingDetail>>;
type AnalysisWire = MeetingDetailWire["analyses"][number];

/** A stored analysis as the detail route serves it, with the jsonb columns
 *  narrowed to the post-enrichment guarantees. */
export type Analysis = Omit<AnalysisWire, "categoryScores" | "notableQuotes"> & {
	categoryScores: Record<CategoryKey, CategoryScore>;
	notableQuotes: NotableQuote[];
};

// ── Meeting detail ───────────────────────────────────────────────────────────

/** `GET /meetings/:id` — derived, with `analyses` narrowed (see `Analysis`). */
export type MeetingDetail = Omit<MeetingDetailWire, "analyses"> & { analyses: Analysis[] };

type MeetingSpeakerWire = MeetingDetailWire["speakers"][number]["meetingSpeaker"];

/** One detail-route speaker join row (enriched with talkTime/airtime), with
 *  `firefliesAnalytics` narrowed to the fields the UI reads (the wire row
 *  carries the full Fireflies block — a superset, so it stays assignable). */
export type MeetingSpeaker = Omit<MeetingSpeakerWire, "firefliesAnalytics"> & {
	firefliesAnalytics: {
		fillerWords: number | null;
		longestMonologueSec: number | null;
		questionsCount: number | null;
		durationPct: number | null; // 0..1
	} | null;
};

/** A stored/queued meeting recording with a short-lived playback URL. */
export type Recording = MeetingDetail["recordings"][number];

/** A who-spoke-when range, used to colour the recording waveform by speaker. */
export type SpeakerTurn = MeetingDetail["turns"][number];

export type TranscriptLine = MeetingDetail["transcript"][number];
export type TranscriptAnnotation = TranscriptLine["annotations"][number];

/** A notable analysis moment pinned to a timestamp on the waveform. UI-only for
 *  now — the route ships `markers: []` until analysis timestamps are captured. */
export type AnalysisMarker = {
	speakerId: string;
	kind: "strength" | "opportunity" | "quote";
	atSec: number;
	label: string;
	sentiment?: "positive" | "negative" | "neutral";
};

// ── Trends / performance / goals ─────────────────────────────────────────────

type SpeakerTrendsWire = Awaited<ReturnType<typeof meetingPerfApi.speakerTrends>>;

/** `GET /speakers/:id/trends` — the raw speaker row plus one point per analysis. */
export type SpeakerTrends = Omit<SpeakerTrendsWire, "speaker"> & {
	speaker: WithAffiliation<SpeakerTrendsWire["speaker"]>;
};

/** One trend point. The trends route omits `segment`; the dashboard's points
 *  carry it — optional here so both series shapes share the name. */
export type TrendPoint = SpeakerTrendsWire["series"][number] & { segment?: string | null };

export type SpeakerPerformance = Awaited<ReturnType<typeof meetingPerfApi.speakerPerformance>>;

/** A per-category goal row (`GET /speakers/:id/goals`). */
export type Goal = Awaited<ReturnType<typeof meetingPerfApi.listGoals>>[number];

// ── Dashboard ────────────────────────────────────────────────────────────────

/** Everything the coaching-home dashboard renders (`GET /dashboard/summary`). */
export type DashboardSummary = Awaited<ReturnType<typeof meetingPerfApi.dashboardSummary>>;

/** A speaker plus their full analysis trend series (oldest-first). */
export type SpeakerWithSeries = DashboardSummary["trackedSpeakers"][number];

/** A recent meeting enriched for the dashboard with its avg score + attendees. */
export type RecentMeeting = DashboardSummary["recentMeetings"][number];

export type MeetingAttendee = RecentMeeting["attendees"][number];

// ── Score color helpers ──────────────────────────────────────────────────────

/** Tailwind text-color class for a 0–100 composite score. */
export function overallScoreColor(score: number): string {
	if (score >= 80) return "text-semantic-success";
	if (score >= 60) return "text-foreground";
	return "text-chart-3"; // amber — needs improvement
}

/** Tailwind text-color class for a 0–10 category score. */
export function categoryScoreColor(score: number): string {
	if (score >= 8) return "text-semantic-success";
	if (score >= 6) return "text-foreground";
	return "text-chart-3";
}
